import React from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Cpu, Settings, Radio, ArrowRight, Thermometer, Layers } from 'lucide-react';
import ResponsiveCarousel from './ResponsiveCarousel';

const Technology = () => {
  const solutions = [
    {
      icon: Wrench,
      title: 'UL101 Aviation Troubleshooter',
      description: 'Our A&SB acoustic ultrasound sensors are designed to provide real-time data on aircraft systems, enabling proactive identification of potential issues and enhancing overall maintenance efficiency.',
      image: '/images/ac641bf0-9cd9-4999-bd54-8c5fce7fbcf4.jpeg',
      badge: null,
      href: '/products/ul101',
      cta: 'Explore the UL101',
    },
    {
      icon: Cpu,
      title: 'InCTRL Aero',
      description: 'Our advanced diagnostic software utilizes cutting-edge technology to analyze data collected wirelessly from the UL101, providing real-time comparison between aircraft components and offering accurate insights into health and performance.',
      image: '/images/63a3ba71-f92d-4c97-ad66-d26707cb161c.png',
      badge: 'AVAILABLE SOON',
      href: null,
      cta: null,
    },
    {
      icon: Radio,
      title: 'Acoustic Cameras',
      description: 'High-performance acoustic imaging cameras combine dense MEMS microphone arrays with HD optical overlays to visualize ultrasonic sound in real time. Pinpoint pressurized gas leaks and partial discharges on avionics and ground-support electrical gear from up to 200 m away.',
      image: '/images/3cbe1c85-ee61-4461-ae94-0963438fbe63.jpeg',
      imagePosition: 'object-top',
      badge: null,
      href: '/products/fotric',
      cta: 'Explore Acoustic Cameras',
    },
    {
      icon: Layers,
      title: '2-in-1 Thermal and Acoustic Cameras',
      description: 'AcouTherm cameras pair a 640×480 radiometric thermal imager with a 162-microphone acoustic array in one handheld. See heat signatures and ultrasonic leaks or partial discharge on the same screen for cross-verified electrical, pneumatic, and hydraulic troubleshooting.',
      image: 'https://static.wixstatic.com/media/571718_fc929bf399b04939949d24f5293c3eee~mv2.png/v1/fill/w_707,h_555,al_c,q_85,enc_auto/vmix.png',
      imageFit: 'object-contain bg-gradient-to-br from-slate-100 to-blue-50 p-6',
      badge: null,
      href: '/products/thermal-acoustic',
      cta: 'Explore 2-in-1 Imagers',
    },
    {
      icon: Thermometer,
      title: 'Thermal Cameras',
      description: 'From compact focus-free kits to 640×480 high-precision imagers with TurboFocus® autofocus — for wiring hot spots, bleed-air and duct leaks, brake and wheel heat checks, de-ice verification, and avionics cooling.',
      image: 'https://static.wixstatic.com/media/571718_1e5eaca495d64cdb83fb6d89cd616141~mv2.jpg/v1/fill/w_800,h_800,al_c,q_85,enc_auto/v-series.jpg',
      imageFit: 'object-contain bg-gradient-to-br from-slate-100 to-blue-50 p-6',
      badge: null,
      href: '/products/thermal-cameras',
      cta: 'Explore Thermal Cameras',
    },
    {
      icon: Settings,
      title: 'Customized Sensor Solutions',
      description: 'CTRL is partnering with operators and OEMs to build custom ultrasonic sensors designed to analyze specific aircraft components and systems. We are always eager to engineer new solutions that meet the specific needs of our clients, ensuring seamless integration and maximum value for their aircraft maintenance operations. Tell us how ultrasound could help address your recurring aircraft maintenance issue, and we can help engineer a solution.',
      image: '/images/c7e7d677-c71a-4d91-a602-405805b757f4.jpeg',
      badge: null,
      href: '/#contact',
      cta: 'Talk to engineering',
    }
  ];

  return (
    <section id="technology" className="py-14 sm:py-20 md:py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e2d3b] mb-4">
            Our <span className="text-blue-600">Solutions</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-orange-500 mx-auto mb-4 sm:mb-6"></div>
          <p className="max-w-3xl mx-auto text-base sm:text-lg md:text-xl text-slate-600 leading-relaxed">
            Cutting-edge ultrasonic and acoustic imaging solutions designed for the unique challenges of aviation maintenance.
          </p>
        </div>

        <ResponsiveCarousel
          breakpoints={{ base: 1, md: 2, lg: 3 }}
          ariaLabel="Our Solutions"
          dataTestId="solutions-carousel"
          autoPlay
          autoPlayInterval={15000}
        >
          {solutions.map((solution, index) => {
            const Icon = solution.icon;
            const cardInner = (
              <>
                <div className="relative h-64 overflow-hidden flex-shrink-0">
                  <img 
                    src={solution.image}
                    alt={solution.title}
                    className={`w-full h-full ${solution.imageFit || 'object-cover'} ${solution.imagePosition || ''} group-hover:scale-110 transition-transform duration-700`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent"></div>
                  {solution.badge && (
                    <div className="absolute top-4 right-4 bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                      {solution.badge}
                    </div>
                  )}
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-white">{solution.title}</h3>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 flex-grow flex flex-col">
                  <p className="text-slate-600 leading-relaxed flex-grow">{solution.description}</p>
                  {solution.href && solution.cta && (
                    <span className="mt-5 inline-flex items-center text-blue-600 font-semibold group-hover:text-orange-500 transition-colors">
                      {solution.cta}
                      <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-1 transition-transform" />
                    </span>
                  )}
                </div>
              </>
            );
            const className = "group bg-white rounded-3xl overflow-hidden border-2 border-slate-100 hover:border-blue-500 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col h-full";
            return solution.href ? (
              <Link
                key={solution.title}
                to={solution.href}
                className={className}
                data-testid={`solution-card-${index}`}
              >
                {cardInner}
              </Link>
            ) : (
              <div key={solution.title} className={className} data-testid={`solution-card-${index}`}>
                {cardInner}
              </div>
            );
          })}
        </ResponsiveCarousel>

        {/* Hidden index (sr-only) — kept in the DOM so search + AI crawlers
            still get every solution URL from the homepage. */}
        <div className="sr-only" data-testid="solutions-index-hidden">
          <h3>Every solution at a glance</h3>
          <ul>
            {solutions.map((s, i) => (
              <li key={s.title}>
                {s.href ? <Link to={s.href} data-testid={`solution-index-link-${i}`}>{s.title}</Link> : <span data-testid={`solution-index-link-${i}`}>{s.title}</span>}
                {s.badge ? ` — ${s.badge}` : ''}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default Technology;
